import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';

import {
  createAsyncEffect,
  getRouteKey,
  noopAction,
  OkDialog,
} from '@hglobal/common';
import { catchError, delay, map, Observable, of, switchMap, tap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { RootState } from '@pt/store';
import { environment } from '@pt/environment';
import { eventEffect } from '@pt/helpers';
import { SupportTip } from '@pt/models';
import { ExtendedRouter, GoogleAnalyticsService } from '@pt/services';

import { SupportActions } from './support.actions';
import { SupportSelectors } from './support.selectors';
import {
  GetCategoriesRes,
  GetTipsReq,
  GetTipsRes,
  SendMessageReq,
  SendMessageRes,
  supportData,
} from '../models';
import { getCategoriesMock, getTipsMock } from '../mocks';
import { TipsDialog } from '../components/tips/tips.dialog';

@Injectable()
export class SupportEffects {
  readonly #googleAnalyticsService = inject(GoogleAnalyticsService);
  readonly #router = inject(ExtendedRouter);

  readonly #navigationEndEffect = eventEffect(
    () => this.#router.navigationEndEvent(),
    () => this._store.dispatch(SupportActions.navigationEnd()),
  );

  init$ = createEffect(() => this._actions$.pipe(
    ofType(SupportActions.init),
    concatLatestFrom(() => this._store.select(SupportSelectors.selectViewModel)),
    map(
      ([, vm]) => (vm.categories.length ? noopAction() : SupportActions.getCategoriesReq.action()),
    ),
  ));

  getTipsReq$ = createEffect(() => this._actions$.pipe(
    ofType(SupportActions.getTipsReq.action),
    switchMap(action => createAsyncEffect(
      this.getTips(action.payload),
      SupportActions.getTipsReq,
    )),
  ));

  openTipsDialog$ = createEffect(
    () => this._actions$.pipe(
      ofType(SupportActions.getTipsReq.succeededAction),
      tap(action => this._tipsDialog.open(action.payload.tips)),
    ),
    { dispatch: false },
  );

  sendMessageReq$ = createEffect(() => this._actions$.pipe(
    ofType(SupportActions.sendMessageReq.action),
    switchMap(action => createAsyncEffect(
      this.sendMessage(action.payload),
      SupportActions.sendMessageReq,
    )),
  ));

  openContactOkDialog$ = createEffect(
    () => this._actions$.pipe(
      ofType(SupportActions.sendMessageReq.succeededAction),
      tap(() => this._okDialog.open({
        headerKey: 'support.contactOkDialog.header',
        messageKey: 'support.contactOkDialog.message',
        subMessageKey: 'support.contactOkDialog.subMessage',
        btnOkKey: 'support.contactOkDialog.btn.close',
        iconName: `${environment.shortName}-small-logo`,
        noCancel: true,
      })),
    ),
    { dispatch: false },
  );

  routerNavigated$ = createEffect(() => this._actions$.pipe(
    ofType(SupportActions.navigationEnd, SupportActions.getCategoriesReq.succeededAction),
    concatLatestFrom(() => this._store.select(SupportSelectors.selectViewModel)),
    switchMap(([, vm]) => {
      const { categoryName, tipType } = supportData[getRouteKey(this._router)] || {};
      const categoryId
        = categoryName ? vm.categories.find(category => category.name === categoryName)?.id : null;

      return [SupportActions.categoryId({ categoryId }), SupportActions.tipType({ tipType })];
    }),
  ));

  getCategoriesReq$ = createEffect(() => this._actions$.pipe(
    ofType(SupportActions.getCategoriesReq.action),
    switchMap(() => createAsyncEffect(this.getCategories(), SupportActions.getCategoriesReq)),
  ));

  constructor(
    private _actions$: Actions,
    private _http: HttpClient,
    private _store: Store<RootState>,
    private _router: Router,
    private _tipsDialog: TipsDialog,
    private _okDialog: OkDialog,
  ) {
  }

  getTips(params: GetTipsReq): Observable<GetTipsRes> {
    if (environment.useMocks) {
      const tips = getTipsMock();

      return of({ itemsCount: tips.length, tips }).pipe(delay(500));
    }

    return this._http.get<SupportTip[]>(`${environment.assetsHost}/assets/tips/${params.type}.json`)
      .pipe(
        map(tips => ({
          itemsCount: tips.length,
          tips,
        })),
      );
  }

  sendMessage(params: SendMessageReq): Observable<SendMessageRes> {
    if (environment.useMocks) {
      return of(null).pipe(delay(5000));
    } else {
      return this._http.post<SendMessageRes>(environment.apiHost + 'support/requests/', params)
        .pipe(
          tap(() => {
            this.#googleAnalyticsService.pushCustomEvent('contact_support', 'send_button', {
              status: 'success',
            });
            this.#googleAnalyticsService.pushCustomEvent('contact_support', 'success', {
              text: params.text,
            });
          }),
          catchError(error => {
            this.#googleAnalyticsService.pushCustomEvent('contact_support', 'send_button', {
              status: error.code,
            });

            throw error;
          }),
        );
    }
  }

  getCategories(): Observable<GetCategoriesRes> {
    if (environment.useMocks) {
      const categories = getCategoriesMock();

      return of({ itemsCount: categories.length, categories }).pipe(delay(500));
    } else {
      return this._http.get<GetCategoriesRes>(
        environment.apiHost + 'news/categories/',
        { params: { articleType: 'faq' } },
      );
    }
  }
}
